import { useEffect, useState } from 'react';
import { MdOutlineClose } from 'react-icons/md';
// import { BiMicrophone } from 'react-icons/bi';
import Input from '../forms/input';
import Select from '../forms/select';
import Textarea from '../forms/textarea';
import apiClient from '../../utils/request';

interface CreateCaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface CaseCategory {
    id: number;
    category: string;
    description: string;
    type: string;
}

const CreateCaseModal: React.FC<CreateCaseModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [isClosing, setIsClosing] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [caseTitle, setCaseTitle] = useState('');
    const [caseCategory, setCaseCategory] = useState('');
    const [caseDetails, setCaseDetails] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState<CaseCategory[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [error, setError] = useState('');

    // Fetch case categories
    useEffect(() => {
        const fetchCategories = async () => {
            setLoadingCategories(true);
            try {
                const response = await apiClient.request<{ response: { data: CaseCategory[] } }>({
                    method: 'get',
                    url: '/settings/categories/case-category',
                });
                if (response.response?.data) {
                    setCategories(response.response.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setLoadingCategories(false);
            }
        };

        if (isOpen) {
            fetchCategories();
        }
    }, [isOpen]);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
            setIsAnimating(false);
            setError('');
        }, 300);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const payload = {
                title: caseTitle.trim(),
                details: caseDetails.trim(),
                categoryId: parseInt(caseCategory, 10),
            };

            const response = await apiClient.request<{
                statusCode: number;
                message?: string;
                response?: { message?: string };
            }>({
                method: 'post',
                url: '/cases/quick-create',
                data: payload,
            });

            if (response?.statusCode === 200 || response?.statusCode === 201) {
                // Reset form
                setCaseTitle('');
                setCaseCategory('');
                setCaseDetails('');

                // Close the create case modal
                handleClose();

                // Notify parent to show success modal
                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess();
                    }, 300);
                }
            } else {
                console.error('Failed to create case:', response?.message || response?.response?.message);
                setError(
                    response?.response?.message || response?.message || 'Failed to create case. Please try again.',
                );
            }
        } catch (error) {
            console.error('Error creating case:', error);
            setError('An error occurred while creating the case. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isSubmitDisabled = !caseTitle.trim() || !caseCategory || !caseDetails.trim() || isLoading;

    // Trigger slide-in animation when modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setIsAnimating(true), 10);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`absolute inset-y-0 left-0 z-500 w-full max-w-md overflow-hidden bg-background shadow-2xl transition-transform duration-400 ease-out ${
                    isClosing ? '-translate-x-full' : isAnimating ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="relative flex h-full flex-col">
                    {/* Background */}
                    <div className="pointer-events-none absolute inset-0 z-0">
                        <img
                            src="/assets/background-skeleton.svg"
                            alt="Perspective grid background"
                            className="absolute bottom-0 left-1/2 w-[200%] -translate-x-1/2 select-none opacity-30"
                        />
                    </div>

                    {/* Header */}
                    <div className="h-[60px] relative z-10 flex items-center justify-between border-b border-[#e1e1e1] px-6 py-5">
                        <img src="/assets/logo-smaill.svg" alt="Podus AI" className="h-auto w-[30px]" />
                        <button
                            onClick={handleClose}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 transition-colors hover:bg-neutral-200"
                            aria-label="Close modal"
                        >
                            <MdOutlineClose size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-1 overflow-y-auto px-6 py-8">
                        <h2 className="mb-2 text-2xl font-bold text-[#151515]">Create Case</h2>
                        <p className="mb-8 text-sm text-neutral-600">
                            We will send the details of your case to a lawyer
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                                    {error}
                                </div>
                            )}

                            {/* Case Title */}
                            <Input
                                id="case-title"
                                name="caseTitle"
                                title="Case Title"
                                placeholder="Case Title"
                                type="text"
                                required
                                value={caseTitle}
                                onChange={(e) => setCaseTitle(e.target.value)}
                            />

                            {/* Case Category */}
                            <Select
                                id="case-category"
                                title="Case Category"
                                required
                                value={caseCategory}
                                onChange={(value) => setCaseCategory(value)}
                                disabled={loadingCategories}
                                placeholder={loadingCategories ? 'Loading categories...' : 'Select a category'}
                                options={categories.map((cat) => ({
                                    value: cat.id.toString(),
                                    label: cat.category,
                                }))}
                            />

                            {/* Details of the Case */}
                            <Textarea
                                id="case-details"
                                name="caseDetails"
                                title="Details of the Case"
                                placeholder="Write the details of the case"
                                required
                                value={caseDetails}
                                onChange={(e) => setCaseDetails(e.target.value)}
                                rows={10}
                            />

                            {/* Voice Note Option */}
                            {/* <div className="flex items-center gap-2 rounded-lg bg-neutral-100 px-4 py-3 text-sm text-neutral-600">
                                <span>You can also send a voice note</span>
                                <button
                                    type="button"
                                    className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-white transition-colors hover:bg-neutral-200"
                                    aria-label="Record voice note"
                                >
                                    <BiMicrophone size={20} />
                                </button>
                            </div> */}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitDisabled}
                                className={`w-full rounded-lg py-3.5 text-sm font-semibold text-white transition-colors ${
                                    isSubmitDisabled
                                        ? 'cursor-not-allowed bg-neutral-400'
                                        : 'bg-[#151515] hover:bg-[#2a2a2a]'
                                }`}
                            >
                                {isLoading ? 'Submitting...' : 'Submit'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateCaseModal;
